import React, { CSSProperties, useState } from 'react';
import { CrownOutlined } from '@ant-design/icons';
import DraftPrepHeader from '../components/DraftPrepHeader';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import { useKeeperOptions } from '../hooks/useDraftPrep';
import { KeeperCandidate, KeeperTeam } from '../types/draftPrep';
import './DraftPrep.css';

const SECTIONS: Array<KeeperCandidate['category']> = ['Likely keeper', 'In consideration', 'Depth'];

const formatOffset = (value?: number | null) => value == null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1)}`;

const CandidateRow: React.FC<{ player: KeeperCandidate }> = ({ player }) => (
  <div className={`keeper-player position-${player.position.toLowerCase()} ${player.isLikelyKeeper ? 'is-likely' : ''}`}>
    <span className="keeper-position">{player.position}</span>
    <div className="keeper-player-copy">
      <strong>{player.name}</strong>
      <span>
        {player.nflTeam || 'FA'} · R{player.redraftRank ?? '—'} · D{player.dynastyRank ?? '—'} · T{player.redraftTier ?? '—'}
        {player.isKeeperOverride && <> · locked</>}
      </span>
    </div>
    <div className="keeper-rank" title="Keeper-adjusted positional rank">
      <strong>{player.weightedRank?.toFixed(1) ?? '—'}</strong>
      <span>adj rank</span>
    </div>
    <div className={`keeper-offset ${(player.dynastyOffset || 0) >= 0 ? 'positive' : 'negative'}`} title="Dynasty boost or penalty">
      {formatOffset(player.dynastyOffset)}
    </div>
  </div>
);

const KeeperTeamCard: React.FC<{ team: KeeperTeam }> = ({ team }) => {
  const style = { '--franchise-color': team.color || '#1a2b4c' } as CSSProperties;
  return (
    <article className="keeper-team-card" style={style}>
      <header>
        <div>
          <strong>{team.teamName}</strong>
          <span>{team.owner || team.franchiseName}</span>
        </div>
        <span>{team.players.filter((player) => player.isLikelyKeeper).length}/5</span>
      </header>
      {SECTIONS.map((section) => {
        const players = team.players.filter((player) => player.category === section);
        if (!players.length) return null;
        return (
          <section className={`keeper-section keeper-section-${section.toLowerCase().replace(' ', '-')}`} key={section}>
            <h2>{section === 'Likely keeper' && <CrownOutlined />} {section}</h2>
            {players.map((player) => <CandidateRow key={player.playerId} player={player} />)}
          </section>
        );
      })}
    </article>
  );
};

const KeeperOptions: React.FC = () => {
  const [season, setSeason] = useState(new Date().getFullYear());
  const [dynastyWeight, setDynastyWeight] = useState(0.25);
  const keepers = useKeeperOptions(season, dynastyWeight);

  return (
    <div className="draft-prep-page keeper-options-page">
      <DraftPrepHeader
        title={`${season} keeper options`}
        description={`Projects five likely keepers from each franchise's ${season - 1} closing roster. Current-year value leads; dynasty rank only breaks the horizon toward players who can realistically occupy one of five slots.`}
        season={season}
        dynastyWeight={dynastyWeight}
        onSeasonChange={setSeason}
        onDynastyWeightChange={setDynastyWeight}
      />

      {keepers.isLoading && <LoadingSpinner size="large" message="Ranking franchise keeper cores…" />}
      {keepers.error && (
        <EmptyState
          message="Keeper options are unavailable"
          description={`${keepers.error.message}. Import rankings, sync the prior season's closing rosters, and regenerate the static export.`}
          icon="🔒"
        />
      )}
      {keepers.data && keepers.data.teams.length === 0 && (
        <EmptyState
          message={`No ${season - 1} closing rosters found`}
          description="Run the Yahoo player-roster sync for the prior season, then refresh this page."
          icon="👥"
        />
      )}
      {keepers.data && keepers.data.teams.length > 0 && (
        <>
          <div className="keeper-explainer">
            <span><strong>Likely keeper</strong> top five normalized values on each roster</span>
            <span><strong>In consideration</strong> positive value over league starter demand</span>
            <span><strong>Depth</strong> below the replacement line or not ranked</span>
            <span><strong>QB 1.10× · RB 1.00× · WR 0.80×</strong> scarcity preference</span>
            <span><strong>TE 0.25×</strong> outside Tier 1; elite TE are 0.90×</span>
            <span><strong>Roster fit</strong> maximum 1 TE; diminishing returns after QB2, RB2, or WR3</span>
            <span><strong>Locked overrides</strong> reviewed season-specific keeper commitments, including offseason trades</span>
          </div>
          <div className="keeper-team-grid">
            {keepers.data.teams.map((team) => <KeeperTeamCard key={team.teamId} team={team} />)}
          </div>
          <footer className="draft-prep-source-note">
            This is a prediction aid, not a submitted-keeper list. Scores compare weighted positional rank with the league's
            20 QB, 20 RB, 30 WR, and 10 TE demand against estimated NFL starter pools of 32 QB, 32 RB,
            60 WR, and 32 TE. A 10% historical QB keeper bias is applied; TE start at a 0.50× scarcity
            factor, with Tier 1 raised to 0.90× and other TE reduced to 0.25× for historical keeper reluctance.
            Projected sets then account for players already selected at each position: a second TE is excluded, while
            surplus QB, RB, and WR remain possible at a reduced marginal value.
          </footer>
        </>
      )}
    </div>
  );
};

export default KeeperOptions;
